import React, { useState } from "react";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { Tooltip } from "@ark-ui/react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import Markdown from "react-markdown";
import {
  ChatBubbleOvalLeftEllipsisIcon,
  HeartIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";

import { makeRequest } from "../../../lib/request";
import Button from "../../atoms/Button";
import { useUserStore } from "../../../store/userStore";

function GalleryActions({ post }: { post: any }) {
  const accessToken = useUserStore((state) => state.accessToken);
  const impersonationToken = useUserStore((state) => state.impersonationToken);
  const user = useUserStore((state) => state.user);

  const [awardModal, setAwardModal] = useState(false);
  const [awardError, setAwardError] = useState(null) as any;
  const [awardType, setAwardType] = useState("ginger");
  const [awards, setAwards] = useState(post.awards);
  const [isLiked, setIsLiked] = useState(post.isLiked);
  const [likes, setLikes] = useState(post._count.likes);
  const [loading, setLoading] = useState(false);

  const likePost = async () => {
    if (user) {
      await makeRequest(`private/gallery/like`, {
        method: "POST",
        body: JSON.stringify({
          postId: post.id,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${impersonationToken || accessToken}`,
        },
      });

      setIsLiked(true);
      setLikes(likes + 1);
    }
  };

  const issueAward = async (awardType: string) => {
    if (loading) {
      return;
    }

    setLoading(true);

    const { status, message, awards } = await makeRequest(
      `private/gallery/award`,
      {
        method: "POST",
        body: JSON.stringify({
          postId: post.id,
          awardType,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${impersonationToken || accessToken}`,
        },
      }
    );

    if (!status) {
      if (message === "none-remaining") {
        setAwardError({
          title: "No more awards!",
          message: (
            <>
              You don't have any more{" "}
              <span className="capitalize">{awardType}</span> awards. Please
              wait until next month for this to renew.
            </>
          ),
        });
      } else if (message === "own-post") {
        setAwardError({
          title: "Try a different post!",
          message: `You can't give an award to your own post, sorry!`,
        });
      } else if (message === "premium-upsell") {
        setAwardError({
          title: "Become a premium member",
          message: (
            <>
              <div>
                To give an award, become a premium member to get one Ginger and
                one Snap award per month!
              </div>
              <div className="mt-5">
                <Button
                  style="secondary"
                  className="px-3 py-2 w-full text-center justify-center gap-2"
                  link={`/premium`}
                >
                  Get premium
                </Button>
              </div>
            </>
          ),
        });
      } else {
        toast.error(message);
      }
    } else {
      setAwardError(null);
      setAwards(awards);
      setAwardModal(false);
      toast.success("Award given!");
    }

    setLoading(false);
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-row flex-wrap gap-3">
          <div className="flex flex-row items-center gap-2">
            <Button
              style="tertiary"
              className={`justify-center flex flex-row items-center text-text-base font-semibold rounded-lg py-1 ps-1 pe-2`}
              link={`/profile/${post.user.nickname}`}
            >
              <span className="flex flex-row gap-2 items-center">
                <span className="h-[2rem] min-w-[2rem] w-[2rem]">
                  <img
                    src={`https://images.givin.gifts/preset:sharp/resize:fit:720:0:0/format:webp/gravity:sm/plain/https://storage.ethereal.sh/gifts-public/${post.user.picture}`}
                    alt=""
                    className="h-full w-full object-cover object-fit rounded-md"
                    width={32}
                    height={32}
                  />
                </span>
                <span>{post.user.nickname}</span>
              </span>
            </Button>
            <span aria-hidden="true" className="text-text-base">
              •
            </span>
            <span className="text-text-base">
              {format(new Date(post.createdAt), "MMM d, yyyy")}
            </span>
          </div>
        </div>
        <div className="text-text-base">
          <Markdown>{post.description}</Markdown>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <div className="bg-surface-raised rounded-xl shadow-sm px-2 py-1 hover:shadow-md hover:bg-surface-lowered flex flex-row gap-1 items-center">
            {isLiked ? (
              <HeartIconSolid className="w-6 h-6 text-text-secondary" />
            ) : (
              <button onClick={likePost}>
                <HeartIcon className="w-6 h-6 text-text-secondary hover:cursor-pointer hover:fill-surface-secondary" />
              </button>
            )}
            <span className="font-semibold text-text-base">{likes}</span>
          </div>
          <div className="bg-surface-raised rounded-xl shadow-sm px-2 py-1">
            <div className="flex flex-row items-center gap-1">
              <span>
                <ChatBubbleOvalLeftEllipsisIcon className="w-6 h-6 text-text-base" />
              </span>{" "}
              <span className="font-semibold text-text-base">
                {post._count.comments}
              </span>
            </div>
          </div>
          <div className="flex flex-row gap-2 items-center ml-auto mr-0">
            <Tooltip.Root closeDelay={1} openDelay={1}>
              <Tooltip.Trigger
                className="flex flex-row items-center gap-2 group/ginger"
                onClick={() => {
                  if (user) {
                    if (!post.isAwarded || !post.isAwarded.ginger) {
                      setAwardError(null);
                      setAwardType("ginger");
                      setAwardModal(true);
                    }
                  } else {
                    toast.error("You must be logged in to give an award.");
                  }
                }}
              >
                <img
                  src={"/assets/award-ginger.svg"}
                  alt="Ginger award"
                  className={`${
                    awards.ginger === 0
                      ? "opacity-30 group-hover/ginger:opacity-100"
                      : ""
                  } w-10 h-10 group-hover/ginger:scale-110 transition-all duration-300 ease-in-out transform`}
                />{" "}
                <span className="font-semibold text-text-base">
                  {awards.ginger}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Positioner>
                <Tooltip.Content className="bg-surface-secondary text-text-inverted p-3 rounded-2xl shadow-xl max-w-[12rem]">
                  {post.isAwarded && post.isAwarded.ginger
                    ? "You have already given a Ginger award."
                    : "Give a Ginger award for an awesome gifter."}
                </Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>
            <Tooltip.Root closeDelay={1} openDelay={1}>
              <Tooltip.Trigger
                className="ml-3 flex flex-row items-center gap-2 group/snap"
                onClick={() => {
                  if (user) {
                    if (!post.isAwarded || !post.isAwarded.snap) {
                      setAwardError(null);
                      setAwardType("snap");
                      setAwardModal(true);
                    }
                  } else {
                    toast.error("You must be logged in to give an award.");
                  }
                }}
              >
                <img
                  src={"/assets/award-snap.svg"}
                  alt="Snap award"
                  className={`${
                    awards.snap === 0
                      ? "opacity-30 group-hover/snap:opacity-100"
                      : ""
                  } w-10 h-10 group-hover/snap:scale-110 transition-all duration-300 ease-in-out transform`}
                />{" "}
                <span className="font-semibold text-text-base">
                  {awards.snap}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Positioner>
                <Tooltip.Content className="bg-surface-secondary text-text-inverted p-3 rounded-2xl shadow-xl max-w-[12rem]">
                  {post.isAwarded && post.isAwarded.snap
                    ? "You have already given a Snap award."
                    : "Give a Snap award for a grateful giftee."}
                </Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>
          </div>
        </div>
      </div>

      <Dialog
        open={awardModal}
        as="div"
        className="relative z-20 focus:outline-none"
        onClose={() => setAwardModal(false)}
      >
        <div className="fixed inset-0 bg-black/50 " aria-hidden="true" />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="w-full max-w-xl bg-surface-base p-6 backdrop-blur-2xl duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0 flex flex-col gap-5 text-text-base">
              <div className="flex flex-row items-center">
                <div>
                  <DialogTitle
                    as="h2"
                    className="font-bold text-text-primary text-2xl"
                  >
                    {awardError ? (
                      <>{awardError.title}</>
                    ) : (
                      <>
                        Give a <span className="capitalize">{awardType}</span>{" "}
                        award
                      </>
                    )}
                  </DialogTitle>
                </div>
                <div className="ml-auto mr-0">
                  <button
                    className="rounded-xl bg-surface-raised p-2 shadow-sm hover:shadow-md hover:bg-surface-lowered"
                    aria-label="Close modal"
                    onClick={() => setAwardModal(false)}
                  >
                    <XMarkIcon className="w-6 h-6 text-text-base" />
                  </button>
                </div>
              </div>
              {awardError ? (
                <div>{awardError.message}</div>
              ) : (
                <>
                  {awardType === "ginger" ? (
                    <p>
                      Give the Ginger award to award the generous gifter! You
                      will receive another Ginger award next month.
                    </p>
                  ) : (
                    <p>
                      Make someone's day with the Snap award to reward a
                      grateful giftee! You will receive another Snap award next
                      month.
                    </p>
                  )}
                  <div className="mt-3 flex">
                    <Button
                      style="secondary"
                      className="px-3 py-2 w-full text-center justify-center gap-2 flex flex-row items-center"
                      onClick={() => issueAward(awardType)}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.5 18.5H7.49998M16.5 18.5C17.2956 18.5 18.0587 18.8161 18.6213 19.3787C19.1839 19.9413 19.5 20.7044 19.5 21.5H4.49998C4.49998 20.7044 4.81605 19.9413 5.37866 19.3787C5.94127 18.8161 6.70433 18.5 7.49998 18.5M16.5 18.5V15.125C16.5 14.504 15.997 14 15.375 14H14.504M7.49998 18.5V15.125C7.49998 14.504 8.00398 14 8.62498 14H9.49698M14.504 14H9.49698M14.504 14C13.9423 13.03 13.6067 11.9457 13.522 10.828M9.49698 14C10.0583 13.0299 10.3936 11.9456 10.478 10.828M13.522 10.828C14.5299 10.5954 15.4697 10.1334 16.27 9.478M13.522 10.828C12.5205 11.059 11.4795 11.059 10.478 10.828M10.478 10.828C9.47038 10.5952 8.53002 10.1333 7.72998 9.478M5.24998 3.986C4.26798 4.129 3.29598 4.303 2.33398 4.506C2.55698 5.81479 3.20756 7.01273 4.18393 7.91238C5.16029 8.81202 6.40736 9.36261 7.72998 9.478M5.24998 3.986V4.25C5.24998 6.358 6.21598 8.24 7.72998 9.478M5.24998 3.986V2.471C7.45598 2.16 9.70998 2 12 2C14.291 2 16.545 2.16 18.75 2.47V3.986M18.75 3.986V4.25C18.75 6.358 17.784 8.24 16.27 9.478M18.75 3.986C19.7273 4.1282 20.6998 4.30163 21.666 4.506C21.443 5.81464 20.7926 7.01246 19.8164 7.91209C18.8403 8.81172 17.5924 9.3624 16.27 9.478"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      <span>
                        <span>Give a </span>
                        <span className="capitalize">{awardType}</span>
                        <span> award</span>
                      </span>
                    </Button>
                  </div>
                </>
              )}
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default GalleryActions;
